import * as React from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useEffect } from 'react';
import VehicleChipsControl from './VehicleChipsControl';
import WaypointsControl from './WaypointsControl';
import AutoFocusControl from './AutoFocusControl';
import TrafficLayerControl from './TrafficLayerControl';
import PermissionsService, {
  PERMISSION_BLUECOINS,
  PERMISSION_WAYPOINT_CUSTOMER,
  PERMISSION_WAYPOINT_PRIVATE,
} from '../../../Services/PermissionsService';
import ZoomControl from './ZoomControl';
import VehicleChipsWindow from './VehicleChipsControl/VehicleChipsWindow';
import SearchControl from './SearchControl';
import SearchBar from './SearchControl/SearchBar';
import FullScreenControl from './FullScreenControl';
import MapControl from './MapControl';
import Waypoints from '../Waypoints/index';
import VehicleFocus from './VehicleFocus';
import PlayerControl from './PlayerControl';
import RidePlayerMenu from '../../RidePlayer/RidePlayerMenu';
import OnlineHelp from '../../OnlineHelp';
import BluecoinsControl from './BluecoinsControl';
import Bluecoins from '../Bluecoins/Bluecoins';
import { selectedCustomersAtom } from '../../../store/recoil/customers';
import { pauseAtom, playAtom } from '../../RidePlayer/RidePlayerState';
import { isOnlinePanelOpenAtom } from '../../OnlinePanel/OnlinePanelState';
import { isBluecoinsOpenAtom } from '../Bluecoins/BluecoinState';
import { isWaypointsOpenAtom } from '../Waypoints/WaypointState';
import {
  isAllSimpleWindowsOpenAtom,
  isRidePlayerMenuOpenAtom,
  isRidePlayerOpenAtom,
  isSearchBarOpenAtom,
  isStreetviewEnabledAtom,
} from '../OnlineMapState';

interface IToolbarProps {
  googleMap: google.maps.Map | undefined;
  trafficLayerControl?: boolean;
  vehicleChipsControl?: boolean;
  vehicleFocusControl: boolean;
  autofocusControl: boolean;
  waypointsControl?: boolean;
  vehicleSearchControl?: boolean;
  bluecoinsControl?: boolean;
}

function ToolbarLayer(props: IToolbarProps): JSX.Element | null {
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const setPause = useSetRecoilState<boolean>(pauseAtom);
  const setPlay = useSetRecoilState<boolean>(playAtom);
  const isAllSimpleWindowsOpen = useRecoilValue<boolean>(isAllSimpleWindowsOpenAtom);
  const isSearchBarOpen = useRecoilValue<boolean>(isSearchBarOpenAtom);
  const isRidePlayerMenuOpen = useRecoilValue<boolean>(isRidePlayerMenuOpenAtom);
  const isOnlinePanelOpen = useRecoilValue<number | false>(isOnlinePanelOpenAtom);
  const isRidePlayerOpen = useRecoilValue<number | boolean>(isRidePlayerOpenAtom);
  const isWaypointsOpen = useRecoilValue<boolean>(isWaypointsOpenAtom);
  const isStreetviewEnabled = useRecoilValue<boolean>(isStreetviewEnabledAtom);
  const isBluecoinsOpen = useRecoilValue<boolean>(isBluecoinsOpenAtom);

  // Pause played ride if ride player menu is open
  useEffect(() => {
    if (isRidePlayerMenuOpen) {
      setPause(true);
      setPlay(false);
    } else {
      setPause(false);
      setPlay(false);
    }
  }, [isRidePlayerMenuOpen, setPause, setPlay]);

  const isVehicleFocusControlEnabled = () => {
    return Boolean(isOnlinePanelOpen && !isWaypointsOpen && !isBluecoinsOpen);
  };

  if (!props.googleMap) {
    return null;
  }

  if (isStreetviewEnabled) {
    return null;
  }

  return (
    <div>
      {!isSearchBarOpen && props.vehicleSearchControl && (
        <div className="b-map-search ">
          <SearchControl />
        </div>
      )}
      {isSearchBarOpen && <SearchBar />}
      <div style={{ position: 'absolute', top: '45px', left: '8px' }}>
        <OnlineHelp topic="map" />
      </div>
      <div className="b-map-custom-control" style={!isBluecoinsOpen ? { zIndex: 2 } : {}}>
        {isRidePlayerOpen && <PlayerControl />}
        {isRidePlayerOpen && isRidePlayerMenuOpen && <RidePlayerMenu />}
        {!isRidePlayerOpen && (
          <>
            {props.bluecoinsControl &&
              PermissionsService.hasAccess(PERMISSION_BLUECOINS, ['read'], selectedCustomers) && (
                <BluecoinsControl />
              )}
            {props.waypointsControl &&
              (PermissionsService.hasAccess(
                PERMISSION_WAYPOINT_CUSTOMER,
                ['read', 'create', 'update', 'delete'],
                selectedCustomers
              ) ||
                PermissionsService.hasAccess(
                  PERMISSION_WAYPOINT_PRIVATE,
                  ['read', 'create', 'update', 'delete'],
                  selectedCustomers
                )) && <WaypointsControl />}

            {isBluecoinsOpen &&
              PermissionsService.hasAccess(PERMISSION_BLUECOINS, ['read'], selectedCustomers) && (
                <Bluecoins map={props.googleMap} />
              )}
            {isWaypointsOpen && <Waypoints />}
            {props.trafficLayerControl && <TrafficLayerControl map={props.googleMap} />}
            {props.vehicleChipsControl && <VehicleChipsControl />}
            {isAllSimpleWindowsOpen && <VehicleChipsWindow />}
          </>
        )}
      </div>

      <div className="b-map-custom-control b-map-custom-control--right">
        <OnlineHelp topic="map-control" />
        <FullScreenControl />
        <MapControl map={props.googleMap} />
        {props.vehicleFocusControl && isVehicleFocusControlEnabled() && <VehicleFocus />}
        {props.autofocusControl && <AutoFocusControl />}
        <ZoomControl map={props.googleMap} />
      </div>
    </div>
  );
}

export default ToolbarLayer;
